'use client';

import { useState, useEffect } from 'react';
import { usePathname } from 'next/navigation';
import { GoogleTagManager } from '@next/third-parties/google';
import Cookies from 'js-cookie';
import TransitionLink from '@/components/TransitionLink';
import { logger } from '@/utils/logger';
import { isEmbedPath } from '@/utils/isEmbedPath';

const GTM_ID = 'GTM-5BDSSKM8';
const IPINFO_TOKEN = 'ba4cbe74895006';

const EU_COUNTRIES = [
  'AT',
  'BE',
  'BG',
  'HR',
  'CY',
  'CZ',
  'DK',
  'EE',
  'FI',
  'FR',
  'DE',
  'GR',
  'HU',
  'IE',
  'IT',
  'LV',
  'LT',
  'LU',
  'MT',
  'NL',
  'PL',
  'PT',
  'RO',
  'SK',
  'SI',
  'ES',
  'SE',
  'IS',
  'LI',
  'NO',
  'GB',
];

export default function CookieConsent() {
  const pathname = usePathname();
  const [cookieState, setCookieState] = useState<
    'not-answered' | 'accepted' | 'rejected' | 'implied'
  >('not-answered');
  const [showBanner, setShowBanner] = useState(false);

  useEffect(() => {
    // Iframe embeds get no banner and no GTM, so skip the geolocation lookup too.
    if (isEmbedPath(window.location.pathname)) return;

    const initConsent = () => {
      // Check for existing consent first — skip geolocation if already decided
      const consentState = Cookies.get('cookie-consent-state') as
        | 'accepted'
        | 'rejected'
        | 'implied'
        | undefined;

      if (consentState) {
        setCookieState(consentState);
        setShowBanner(false);
        return;
      }

      // Check localStorage cache for geolocation result
      const cachedCountry = localStorage.getItem('user-country');

      const resolveConsent = (country: string) => {
        const isEU = EU_COUNTRIES.includes(country);
        if (!isEU) {
          Cookies.set('cookie-consent-state', 'implied', { expires: 365 });
          setCookieState('implied');
          setShowBanner(false);
        } else {
          setShowBanner(true);
        }
      };

      if (cachedCountry) {
        resolveConsent(cachedCountry);
        return;
      }

      const checkLocation = async () => {
        try {
          const response = await fetch(`https://ipinfo.io/json?token=${IPINFO_TOKEN}`);
          const data = await response.json();
          localStorage.setItem('user-country', data.country);
          resolveConsent(data.country);
        } catch (error) {
          logger.error('Error checking location:', error);
          Cookies.set('cookie-consent-state', 'implied', { expires: 365 });
          setCookieState('implied');
          setShowBanner(false);
        }
      };

      checkLocation();
    };

    // Defer to avoid blocking the main thread during page load
    if ('requestIdleCallback' in window) {
      requestIdleCallback(initConsent);
    } else {
      setTimeout(initConsent, 1);
    }
  }, []);

  const handleConsent = (state: 'accepted' | 'rejected') => {
    Cookies.set('cookie-consent-state', state, { expires: 365 });
    setCookieState(state);
    setShowBanner(false);
  };

  // No consent banner and no GTM inside third-party iframe embeds.
  if (isEmbedPath(pathname)) return null;

  if (showBanner && cookieState === 'not-answered') {
    return (
      <div className="fixed bottom-0 left-0 right-0 z-30 bg-black p-6 py-6">
        <p className="mb-4 text-blue-200">
          Our website{' '}
          <TransitionLink className="text-blue-400 underline" href="/en-us/privacy">
            utilizes cookies
          </TransitionLink>{' '}
          to enhance your experience. Click <b>&apos;Accept&apos;</b> to allow them or{' '}
          <b>&apos;No thanks&apos;</b> to opt out.{' '}
        </p>
        <button
          className="mr-4 min-w-36 rounded-sm border border-blue bg-blue-500 py-1 text-sm font-bold uppercase text-white"
          onClick={() => handleConsent('accepted')}
        >
          Accept
        </button>
        <button
          className="min-w-36 rounded-sm border border-blue py-1 text-sm font-bold uppercase text-blue-500"
          onClick={() => handleConsent('rejected')}
        >
          No Thanks
        </button>
      </div>
    );
  }

  // Prevent GTM from loading on localhost
  if (typeof window !== 'undefined' && window.location.hostname === 'localhost') {
    return null;
  }

  // Load GTM for both explicit and implied consent
  if (cookieState === 'accepted' || cookieState === 'implied') {
    return <GoogleTagManager gtmId={GTM_ID} />;
  }

  return null;
}
